// Tab-aware shell for the persistent right-side panel. Replaces the contextual
// InfoPanelShell pattern at the outer level — provides chrome (header, tab bar,
// scroll area) for any combination of tab content. Selection tabs can opt in
// to an info button (ⓘ) in the header to open modals like EntityModal.
//
// Width is responsive via Tailwind: 320px at md+ (768px), 380px at xl+ (1280px).
// Hidden below md so mobile keeps a canvas-dominant layout.
//
// Visual style uses the same `scroll_bg` gradient as InfoPanelShell so the right
// rail has the same visual weight as DoctrinePanel on the left. The legacy
// `info-panel-compact` class is intentionally NOT applied — its global CSS rule
// forces `width: 240px !important` below 1280px, which would override the
// responsive widths above.

import { Button } from '@aod/ui/components/ui/button';
import { Info, ScrollText, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import {
  PanelCollapseButton,
  PanelDockGrip,
  PanelRailButton,
  PanelResizeHandle,
  useIsDesktop,
  usePanelDockStyle,
} from '../layout/PanelDockControls.js';
import { usePanelLayout } from '../layout/usePanelLayout.js';
import { shouldJumpToSelectionTab } from './tabFollow.js';

export interface TabDef {
  readonly id: string;
  readonly label: string;
  readonly content: ReactNode;
  readonly dismissible?: boolean;
  readonly onDismiss?: () => void;
  readonly onInfo?: () => void;
  /** A count worth noticing on the tab (offers waiting); hidden at 0. */
  readonly badge?: number;
  /** Spoken form of the badge. */
  readonly badgeLabel?: string;
}

interface RightPanelShellProps {
  readonly title: string;
  readonly tabs: readonly TabDef[];
  readonly defaultTabId?: string;
  readonly className?: string;
  /** Below md the rail is a bottom sheet the "Info" toggle opens (hidden otherwise). */
  readonly mobileOpen?: boolean;
  /** Phone only: identity of the current selection; a change jumps to the `selection` tab. */
  readonly followSelectionKey?: string | null;
}

export function RightPanelShell({
  title,
  tabs,
  defaultTabId,
  className,
  mobileOpen = false,
  followSelectionKey,
}: RightPanelShellProps) {
  const t = useT();
  const initial =
    defaultTabId && tabs.some((t) => t.id === defaultTabId) ? defaultTabId : (tabs[0]?.id ?? '');
  const [activeTabId, setActiveTabId] = useState(initial);

  // Phone: a new selection brings its tab forward (a later deliberate tab pick sticks).
  const follow = followSelectionKey !== undefined;
  const [seenKey, setSeenKey] = useState<string | null>(null);
  const key = followSelectionKey ?? null;
  if (follow && key !== seenKey) {
    setSeenKey(key);
    if (
      shouldJumpToSelectionTab({
        follow,
        previousKey: seenKey,
        key,
        hasSelectionTab: tabs.some((t) => t.id === 'selection'),
      })
    ) {
      setActiveTabId('selection');
    }
  }

  // If the active tab is removed (e.g., selection cleared), fall back to the first tab.
  if (activeTabId !== '' && !tabs.some((t) => t.id === activeTabId)) {
    setActiveTabId(tabs[0]?.id ?? '');
  }

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0];

  // Panel-dock store (desktop only; the phone bottom-sheet keeps today's
  // behaviour below md).
  const dockStyle = usePanelDockStyle('info', true);
  const collapsed = usePanelLayout((s) => s.panels.info.collapsed);
  const isDesktop = useIsDesktop();
  if (collapsed && isDesktop) {
    return (
      <PanelRailButton
        id="info"
        label={title || t('rightPanel.label')}
        icon={<ScrollText className="size-4" />}
      />
    );
  }

  return (
    <aside
      aria-label={title}
      data-testid="right-panel"
      {...appDom({
        id: 'right-panel',
        type: 'Sidebar',
        scope: 'aside',
        label: t('rightPanel.label'),
      })}
      style={{
        background: `linear-gradient(var(--panel-scrim), var(--panel-scrim)), url('/assets/images/ui/scroll_bg.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        ...dockStyle,
      }}
      className={`fixed top-(--hud-panel-top) right-3 bottom-(--right-rail-bottom) z-40 ${
        mobileOpen
          ? 'flex max-md:top-auto max-md:bottom-16 max-md:left-3 max-md:max-h-[60vh] max-md:w-auto'
          : 'hidden md:flex'
      } w-(--rail-w) flex-col gap-2 rounded-xl border border-border/70 p-3 text-foreground shadow-xl backdrop-blur-sm ${
        className ?? ''
      }`.trim()}
    >
      <header className="flex items-start justify-between gap-2 px-1 pt-1 pb-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground">
            {t('rightPanel.info')}
          </span>
          <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
        </div>
        <div className="flex shrink-0 gap-0.5 -mt-0.5">
          {/* Dock affordances ride on the header's button cluster. */}
          <PanelDockGrip id="info" />
          <PanelCollapseButton id="info" />
          {activeTab?.onInfo ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('rightPanel.viewDetails')}
              onClick={activeTab.onInfo}
              className="size-7 text-muted-foreground hover:text-foreground"
            >
              <Info className="size-4" />
            </Button>
          ) : null}
          {activeTab?.dismissible && activeTab.onDismiss ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('rightPanel.closeSelection')}
              onClick={activeTab.onDismiss}
              className="size-7 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </Button>
          ) : null}
        </div>
      </header>
      <div role="tablist" className="flex items-center gap-1">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTabId(tab.id)}
              className={
                isActive
                  ? 'flex-1 rounded-md px-2 py-1 text-[11px] font-semibold tracking-wide transition-colors bg-primary/10 text-primary'
                  : 'flex-1 rounded-md px-2 py-1 text-[11px] font-semibold tracking-wide transition-colors text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              }
            >
              {tab.label}
              {tab.badge ? (
                <span
                  className="ml-1 rounded-full bg-emerald-500/20 px-1.5 text-[10px] text-emerald-700 tabular-nums dark:text-emerald-300"
                  data-testid={`tab-badge-${tab.id}`}
                  role="img"
                  aria-label={tab.badgeLabel}
                >
                  {tab.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling panel must be keyboard reachable */}
      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1" tabIndex={0}>
        <div className="flex flex-col gap-3 py-1">{activeTab?.content}</div>
      </div>
      <PanelResizeHandle id="info" side="left" />
    </aside>
  );
}
